import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  User,
  Tag,
  FileText,
  Users,
  Building,
  Bell,
  Settings,
  Loader2,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import { useDebounce } from '../hooks/useDebounce';
import api from '../lib/api';
import { getSearchResultPath, getSearchResultValue, type SearchResult } from '../lib/searchResults';

const navigationCommands = [
  {
    id: 'transactions',
    name: 'Transactions',
    description: 'View recent insider transactions',
    icon: FileText,
    path: '/transactions',
  },
  {
    id: 'insiders',
    name: 'Insiders',
    description: 'Browse insider profiles',
    icon: Users,
    path: '/insiders',
  },
  {
    id: 'companies',
    name: 'Companies',
    description: 'Browse company data',
    icon: Building,
    path: '/companies',
  },
  {
    id: 'alerts',
    name: 'Alerts',
    description: 'Manage your alerts',
    icon: Bell,
    path: '/alerts',
  },
  {
    id: 'settings',
    name: 'Settings',
    description: 'Manage your account',
    icon: Settings,
    path: '/settings',
  },
];

export function CommandPalette() {
  const navigate = useNavigate();
  const { isOpen, close } = useCommandPaletteStore();
  const [query, setQuery] = useState('');
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  const debouncedQuery = useDebounce(query, 300);

  // Search API query
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['command-search', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) return [];
      const response = await api.get<SearchResult[]>(
        `/search/?q=${encodeURIComponent(debouncedQuery)}`
      );
      return response.data;
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 0,
  });

  // Register keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        useCommandPaletteStore.getState().toggle();
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Reset query when dialog closes
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElement.current = document.activeElement as HTMLElement | null;
      return;
    }

    if (!isOpen) {
      setQuery('');
      window.setTimeout(() => previouslyFocusedElement.current?.focus?.(), 0);
    }
  }, [isOpen]);

  const handleSelect = useCallback(
    (callback: () => void) => {
      close();
      callback();
    },
    [close]
  );

  const getResultIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'insider':
        return <User className="mr-2 h-4 w-4 text-primary-600" />;
      case 'company':
        return <Building2 className="mr-2 h-4 w-4 text-success-600" />;
      case 'ticker':
        return <Tag className="mr-2 h-4 w-4 text-warning-600" />;
    }
  };

  const handleResultClick = (result: SearchResult) => {
    const destination = getSearchResultPath(result);
    if (!destination) {
      return;
    }

    handleSelect(() => {
      navigate(destination);
    });
  };

  const showNavigationCommands = !query || query.length < 2;
  const showSearchResults = query.length >= 2;

  return (
    <CommandDialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <CommandInput
        placeholder="Search companies, insiders, or type a command..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {isLoading && showSearchResults && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
          </div>
        )}

        {!isLoading && showSearchResults && searchResults && searchResults.length === 0 && (
          <CommandEmpty>No results found for "{query}"</CommandEmpty>
        )}

        {showSearchResults && searchResults && searchResults.length > 0 && (
          <>
            {/* Companies */}
            {searchResults.filter((r) => r.type === 'company' || r.type === 'ticker').length >
              0 && (
              <CommandGroup heading="Companies">
                {searchResults
                  .filter((r) => r.type === 'company' || r.type === 'ticker')
                  .slice(0, 5)
                  .map((result) => (
                    <CommandItem
                      key={`${result.type}-${result.id}`}
                      value={getSearchResultValue(result)}
                      onSelect={() => handleResultClick(result)}
                      className="cursor-pointer"
                    >
                      {getResultIcon(result.type)}
                      <div className="flex flex-col">
                        <span>{result.name}</span>
                        {result.subtitle && (
                          <span className="text-xs text-muted-foreground">{result.subtitle}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
              </CommandGroup>
            )}

            {/* Insiders */}
            {searchResults.filter((r) => r.type === 'insider').length > 0 && (
              <CommandGroup heading="Insiders">
                {searchResults
                  .filter((r) => r.type === 'insider')
                  .slice(0, 5)
                  .map((result) => (
                    <CommandItem
                      key={`${result.type}-${result.id}`}
                      value={getSearchResultValue(result)}
                      onSelect={() => handleResultClick(result)}
                      className="cursor-pointer"
                    >
                      {getResultIcon(result.type)}
                      <div className="flex flex-col">
                        <span>{result.name}</span>
                        {result.subtitle && (
                          <span className="text-xs text-muted-foreground">{result.subtitle}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
              </CommandGroup>
            )}
          </>
        )}

        {showNavigationCommands && (
          <>
            <CommandGroup heading="Navigation">
              {navigationCommands.map((command) => (
                <CommandItem
                  key={command.id}
                  onSelect={() => handleSelect(() => navigate(command.path))}
                  className="cursor-pointer"
                >
                  <command.icon className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span>{command.name}</span>
                    <span className="text-xs text-muted-foreground">{command.description}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
